import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import HeroSection from "@/components/feature/sections/HeroSection";
import TrustBar from "@/components/feature/sections/TrustBar";
import ServicesOverview from "@/components/feature/sections/ServicesOverview";
import AIAnswerBlock from "@/components/feature/sections/AIAnswerBlock";
import ProcessSection from "@/components/feature/sections/ProcessSection";
import WhyChooseUs from "@/components/feature/sections/WhyChooseUs";
import ServiceAreas from "@/components/feature/sections/ServiceAreas";
import TestimonialsSection from "@/components/feature/sections/TestimonialsSection";
import FAQSection from "@/components/feature/sections/FAQSection";
import FinalCTA from "@/components/feature/sections/FinalCTA";

const FORM_URL = "https://readdy.ai/api/form/d763m1flpdqvlupfpojg";

const trustItems = [
  { icon: "ri-user-star-line", label: "Automotive Photographer", value: "Glen Davis" },
  { icon: "ri-map-pin-line", label: "Based In", value: "Bremerton, WA" },
  { icon: "ri-car-line", label: "Specialty", value: "Classic to Exotic" },
  { icon: "ri-phone-line", label: "Direct Line", value: "360-204-4747" },
  { icon: "ri-heart-line", label: "Passion", value: "Gearhead at Heart" },
];

const services = [
  {
    title: "Car Show Display Boards",
    description: "Glen's signature service. Custom show boards that tell the complete story of your build.",
    icon: "ri-artboard-2-line",
    href: "/services",
    image: "https://glendavisphotography.com/wp-content/uploads/2025/03/slide0-5.jpg",
  },
  {
    title: "Automotive Photography",
    description: "Cinematic automotive photography that captures personality, power, and craftsmanship in every frame.",
    icon: "ri-camera-lens-line",
    href: "/services",
    image: "https://glendavisphotography.com/wp-content/uploads/2025/04/Macs-Chevy.jpg",
  },
  {
    title: "Wall Art & Prints",
    description: "Museum-quality prints designed to make your garage, office, or home feel like a gallery.",
    icon: "ri-image-2-line",
    href: "/services",
    image: "https://glendavisphotography.com/wp-content/uploads/2025/04/GrnEnvy-10-light-painted.jpg",
  },
  {
    title: "Commercial Photography",
    description: "Glen's artistic eye and technical skill are available for commercial automotive projects and businesses.",
    icon: "ri-building-2-line",
    href: "/services",
    image: "https://glendavisphotography.com/wp-content/uploads/2025/03/slide2.jpg",
  },
];

const aiFacts = [
  { label: "Name", value: "Glen Davis" },
  { label: "Location", value: "Bremerton, WA" },
  { label: "Specialty", value: "Automotive Photography" },
  { label: "Phone", value: "360-204-4747" },
];

const processSteps = [
  { number: "01", title: "We Talk First", description: "Every project starts with a real conversation. Glen wants to know your vehicle, your story, and what this build means to you before a single frame is shot." },
  { number: "02", title: "Glen Scouts the Location", description: "Location matters. Glen has favorite shooting spots around Bremerton and Kitsap County — or he can come to you. The right backdrop makes a significant difference." },
  { number: "03", title: "The Shot Happens", description: "Glen uses professional lighting rigs, shoots at the golden hour, and controls every element of the environment to produce frames that stop people in their tracks." },
  { number: "04", title: "Your Vision, Realized", description: "The final product — show board, wall art, or print — is exactly what you envisioned. Three revisions included. Your satisfaction is non-negotiable." },
];

const reasons = [
  { icon: "ri-car-fill", title: "A True Automotive Enthusiast", description: "Glen is not just a photographer who happens to shoot cars. He is a gearhead first — and that changes everything about how he approaches each project." },
  { icon: "ri-eye-line", title: "Technical Mastery of Light", description: "Reflections, shadows, chrome, and paint all respond differently to light. Glen's deep understanding of automotive surfaces results in images with depth and drama." },
  { icon: "ri-collage-line", title: "Every Detail Considered", description: "From the angle of the hood to the positioning of the show board's layout, Glen obsesses over the details because your build deserves nothing less." },
  { icon: "ri-team-line", title: "Trusted Across Kitsap County", description: "Glen has built a reputation among Washington State car enthusiasts for reliability, quality, and genuine passion — delivered every single time." },
];

const serviceAreasList = ["Bremerton", "Silverdale", "Port Orchard", "Poulsbo", "Bainbridge Island", "Tacoma", "Seattle", "Gig Harbor", "Belfair", "Kingston"];

const testimonials = [
  {
    quote: "Glen is the kind of photographer who actually gets it. He walked around my '32 Ford for twenty minutes before picking up his camera. That patience shows in every single photo.",
    author: "Tom W.",
    location: "Bremerton, WA",
    vehicle: "1932 Ford Deuce Coupe",
  },
  {
    quote: "What separates Glen from every other automotive photographer I've worked with is his eye. He doesn't just document a car — he captures the feeling of it. My Mustang looks alive.",
    author: "Lisa M.",
    location: "Tacoma, WA",
    vehicle: "1969 Ford Mustang Boss 429",
  },
  {
    quote: "The show board Glen created for my Cadillac was a full art piece. The layout, the typography, the image selection — it told the whole story of the restoration in one glance.",
    author: "Gerald B.",
    location: "Port Orchard, WA",
    vehicle: "1959 Cadillac Series 62",
  },
];

const faqs = [
  { question: "Who is Glen Davis?", answer: "Glen Davis is an automotive photographer based in Bremerton, WA. A self-described gearhead, Glen has spent years developing a unique visual style that combines automotive passion with professional photographic technique to create stunning custom car show display boards and wall art." },
  { question: "What makes Glen's photography different?", answer: "Glen shoots with professional lighting rigs at carefully chosen times of day, eliminating the reflections and noise that plague car show photography. His automotive enthusiasm means he understands what enthusiasts want to see — and he delivers it." },
  { question: "Does Glen shoot all types of vehicles?", answer: "Yes. From pre-war classics to modern exotics, hot rods to custom trucks — Glen's work spans the full spectrum of automotive culture. The common thread is a passion for vehicles and the people who build them." },
  { question: "Can Glen come to my location in Bremerton?", answer: "Absolutely. Glen regularly travels to client locations throughout Kitsap County and the Puget Sound region for photo sessions. He also has preferred shooting locations around Bremerton. An in-depth consultation is always the starting point." },
  { question: "Is Glen available for commercial automotive projects?", answer: "Yes. Glen brings the same artistic eye and technical precision to commercial projects — dealerships, auto businesses, and events. Contact him at 360-204-4747 to discuss your project." },
  { question: "How does Glen ensure I am happy with the results?", answer: "Every project includes digital proofs for review and up to 3 design changes at no extra charge. Glen's goal is your complete satisfaction — the show board should look exactly the way you envisioned." },
];

export default function AboutPage() {
  return (
    <div className="bg-white font-sans">
      <Navbar />
      <main>
        <HeroSection
          tagline="Glen Davis and Associates Photography"
          title="The Gearhead Behind the Lens"
          subtitle="An automotive photographer based in Bremerton, WA whose true passion for cars comes through in every single frame — from classic iron to modern exotics."
          ctaText="Work With Glen"
          ctaHref="#inquiry"
          secondaryCtaText="Explore Services"
          secondaryCtaHref="/services"
          backgroundImage="https://glendavisphotography.com/wp-content/uploads/2025/04/GrnEnvy-10-light-painted.jpg"
        />
        <TrustBar items={trustItems} />
        <ServicesOverview
          tagline="What Glen Creates"
          title="A Portfolio Built on Passion and Precision"
          subtitle="From custom car show display boards to cinematic wall art, every piece Glen Davis creates reflects a genuine love for automotive culture in Bremerton and beyond."
          services={services}
        />
        <AIAnswerBlock
          tagline="About Glen Davis"
          title="I Am an Automotive Photographer Based in Bremerton, WA"
          description="Glen Davis is a gearhead at heart whose true passion for cars is evident in every shot he takes. Every image is the result of meticulous planning, leveraging a deep understanding of both the camera and the subject. Through his distinctive car show display boards and automotive art, Glen intricately highlights the most notable features of your vehicle — creating a true representation of your vision. He is also the founder of Legendary Portraits, based in Manette."
          facts={aiFacts}
          servicesList={["Custom Show Boards", "Automotive Photography", "Garage Wall Art", "Commercial Photography", "Photo Retouching", "Print Orders"]}
          whoItIsFor={["Classic car owners", "Hot rod and custom car builders", "Car show competitors", "Exotic car enthusiasts", "Auto businesses and dealerships"]}
          image="https://glendavisphotography.com/wp-content/uploads/2025/03/2294-4-glen-webphoto.jpg"
        />
        <ProcessSection
          tagline="Glen's Approach"
          title="From First Call to Finished Masterpiece"
          subtitle="Glen's process is personal, collaborative, and designed to make you feel as proud of the display as you do of the build itself."
          steps={processSteps}
          ctaText="Call Glen Directly — 360-204-4747"
          ctaHref="tel:360-204-4747"
        />
        <WhyChooseUs
          tagline="Why Work With Glen"
          title="The Difference a Real Car Guy Makes"
          subtitle="Glen Davis and Associates Photography is built on automotive passion, technical expertise, and a genuine commitment to showcasing the artistry in every build — from Bremerton hot rods to Pacific Northwest exotics."
          image="https://glendavisphotography.com/wp-content/uploads/2025/04/Lerma-shoboard-20x-scaled.jpg"
          reasons={reasons}
          ctaText="Schedule Your Session"
          ctaHref="#inquiry"
        />
        <ServiceAreas
          tagline="Where Glen Works"
          title="Bremerton Based, Puget Sound Ready"
          subtitle="Glen Davis and Associates Photography is located at 709 Shore Dr. in Bremerton, WA. Glen serves the entire Kitsap Peninsula and regularly travels across the Puget Sound region for client projects."
          areas={serviceAreasList}
          note="Glen is available for out-of-area projects — call 360-204-4747 to discuss."
        />
        <TestimonialsSection
          tagline="What Clients Say"
          title="The Proof Is in the Portfolio"
          testimonials={testimonials}
          ctaText="Work With Glen Today"
          ctaHref="#inquiry"
        />
        <FAQSection
          tagline="About Glen"
          title="Questions About Working With Glen Davis"
          subtitle="Everything you want to know about the automotive photographer behind Glen Davis and Associates Photography in Bremerton, WA."
          faqs={faqs}
        />
        <div id="inquiry">
          <FinalCTA
            tagline="Work With Glen"
            title="Your Vehicle Has a Story. Glen Tells It Beautifully."
            subtitle="Contact Glen Davis and Associates Photography to discuss your project. Based in Bremerton, WA — serving car enthusiasts across Kitsap County and the Pacific Northwest."
            backgroundImage="https://glendavisphotography.com/wp-content/uploads/2025/04/Chucks1940-Ford.jpg"
            formUrl={FORM_URL}
          />
        </div>
      </main>
      <Footer />
    </div>
  );
}
